import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Mail,
  Plus,
  Send,
  Trash2,
  XCircle,
} from "lucide-react";
import {
  Badge,
  Button,
  OverflowMenu,
  type OverflowMenuItem,
} from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { StagedSubmissionReviewDrawer } from "@/components/Collection/StagedSubmissionReviewDrawer";
import { ItemStatusBadge } from "@/components/Collection/ItemStatusBadge";
import { PACKAGE_STATUS, reviewRowStatus } from "@/components/Collection/collectionStatusLabels";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { useDateFormatter } from "@/utils/datetime";
import { extractErrorMessage } from "@/api/client";
import {
  collectionPackagesApi,
  externalRecipientsApi,
  type CollectionPackageDto,
  type CollectionPackageStatus,
} from "@/api/collectionPackages";
import {
  collectionStagingApi,
  type StagedSubmissionListItem,
} from "@/api/collectionStaging";
import {
  collectionTemplatesApi,
  type CollectionTemplateDto,
} from "@/api/collectionTemplates";
import { portalAccountsApi } from "@/api/portalAccounts";
import { collectionPackagesKeys } from "@/hooks/collection/collectionPackagesKeys";
import { portalAccountsKeys } from "@/hooks/portalAccounts/portalAccountsKeys";
import { usePortalInvitation } from "@/hooks/collection/usePortalInvitation";
import { fallbackOwnerLabel, useOwnerLabels } from "@/api/ownerLabels";
import { formatCalendarDate } from "@/utils/datetime";
import { ApprovalChainComposerPanel } from "@/components/Collection/ApprovalChain/ApprovalChainComposerPanel";
import { ApprovalChainTimeline } from "@/components/Collection/ApprovalChain/ApprovalChainTimeline";
import {
  SendBlockedList,
  sendBlockedProblems,
  useChainReadiness,
} from "@/components/Collection/ApprovalChain/chainSend";
import { invalidateApprovalChainReads } from "@/hooks/collection/approvalChainKeys";
import { APPROVAL_CHAIN_STATUS_TONE } from "@/components/Collection/collectionStatusLabels";

// External Intake & Collection v1.0 PR 8 (Series 1) — staff detail view
// for a CollectionPackage. Mirrors the recipient-facing
// portal/pages/PackageView.tsx so support can answer "what does the
// recipient see right now?" by glancing at the same data. Resend +
// cancel always available; add/remove items only while Draft.

// Review loop — label and tone from the one staff label map; the raw
// CollectionPackageStatus code never renders.
function PackageStatusBadge({ status }: { status: CollectionPackageStatus }) {
  const s = PACKAGE_STATUS[status];
  return <Badge status={s?.tone ?? "draft"}>{s?.label ?? "—"}</Badge>;
}

export function CollectionPackageDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();
  // When opened from an External Account detail page, `from` carries the
  // originating portal-account id so the breadcrumb + back link can return
  // to that specific account rather than the registry list.
  const fromAccountId = params.get("from");
  // WS1 — "Show archived" is URL-persisted and parsed at RENDER time (no
  // post-mount effect — the page-double-fetch rule). When on, the detail read
  // includes archived items so each can be Unarchived; soft-deleted items never
  // render.
  const showArchived = params.get("archived") === "1";
  const setShowArchived = (next: boolean) => {
    const p = new URLSearchParams(params);
    if (next) p.set("archived", "1");
    else p.delete("archived");
    setParams(p, { replace: true });
  };
  const qc = useQueryClient();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const { dateTime } = useDateFormatter();
  const [templateToAdd, setTemplateToAdd] = useState("");
  // F7 — open the shared review drawer in place rather than navigating to the
  // /collection/staging queue (which dropped all package context and stranded
  // staff there).
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  // Approval chains (D4) — every draft chain item reports whether the package
  // may send; a 422 send refusal lists each problem the server named.
  const chainReadiness = useChainReadiness();
  const [sendProblems, setSendProblems] = useState<string[]>([]);

  const fromAccountQuery = useQuery({
    queryKey: ["portal-account", fromAccountId],
    queryFn: () => portalAccountsApi.get(fromAccountId!),
    enabled: Boolean(fromAccountId),
  });

  const canManage = hasPermission("Collection.ManagePackages");
  const canSend = hasPermission("Collection.SendPackages");
  // WS1 — the Archive/soft-Delete affordance shows for either the authoring
  // grant or the dedicated ArchivePackages grant (the server is the real gate:
  // CanArchiveOrDeleteAsync also admits account-assignees and Forbid()s otherwise).
  const canArchive = canManage || hasPermission("Collection.ArchivePackages");

  const pkgQuery = useQuery({
    queryKey: ["collection-package", id, showArchived],
    queryFn: () => collectionPackagesApi.get(id, { includeArchived: showArchived }),
    enabled: Boolean(id),
  });
  const pkg: CollectionPackageDto | undefined = pkgQuery.data;

  useBreadcrumbs(
    fromAccountId
      ? [
          { label: "External Collection" },
          { label: "Accounts", href: "/admin/portal-accounts" },
          {
            label: fromAccountQuery.data?.fullName ?? "Account",
            href: `/admin/portal-accounts/${fromAccountId}`,
          },
          { label: pkg ? `Package #${pkg.id.slice(0, 8)}` : "Package" },
        ]
      : [
          { label: "External Collection" },
          { label: "Document Requests" },
          { label: pkg ? `Package #${pkg.id.slice(0, 8)}` : "Package" },
        ],
  );

  const recipientQuery = useQuery({
    queryKey: ["external-recipient", pkg?.recipientId],
    queryFn: () => externalRecipientsApi.get(pkg!.recipientId),
    enabled: Boolean(pkg),
  });

  const templatesQuery = useQuery({
    queryKey: ["collection-templates", "active"],
    queryFn: () => collectionTemplatesApi.list(false),
    enabled: pkg?.status === "Draft",
  });
  const templates: CollectionTemplateDto[] = templatesQuery.data ?? [];
  const usedTemplateIds = new Set(pkg?.items.map((i) => i.templateId) ?? []);

  // Account-First Phase 2 — inline review. Pulls every staged submission
  // for this package (includeReviewed=true so Approved + Returned rows
  // stay visible alongside AwaitingReview ones; Superseded rows are
  // filtered server-side).
  const submissionsQuery = useQuery({
    queryKey: ["collection-staging", "by-package", id],
    queryFn: () =>
      collectionStagingApi.list({ packageId: id, includeReviewed: true }),
    enabled: Boolean(id) && pkg?.status !== "Draft",
  });
  const submissionsByItem = (() => {
    const m = new Map<string, StagedSubmissionListItem[]>();
    for (const s of submissionsQuery.data?.items ?? []) {
      const arr = m.get(s.collectionItemId) ?? [];
      arr.push(s);
      m.set(s.collectionItemId, arr);
    }
    return m;
  })();

  // PROB-04 — resolve the owner GUID in the page eyebrow.
  const ownerRefs = pkg ? [{ type: pkg.ownerEntityType, id: pkg.ownerEntityId }] : [];
  const owners = useOwnerLabels(ownerRefs);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["collection-package", id] });
    void qc.invalidateQueries({
      queryKey: ["collection-staging", "by-package", id],
    });
    // WS1 — the account detail page renders the same packages + progress, so an
    // Archive/soft-Delete here must refresh its reads too. Broad prefixes (the
    // NewPage precedent) match every account's cache entry.
    void qc.invalidateQueries({
      queryKey: collectionPackagesKeys.portalAccountPackagesPrefix,
    });
    void qc.invalidateQueries({
      queryKey: collectionPackagesKeys.portalAccountSummaryPrefix,
    });
    void qc.invalidateQueries({ queryKey: portalAccountsKeys.all() });
    if (pkg) {
      void qc.invalidateQueries({
        queryKey: [
          "collection-packages",
          pkg.ownerEntityType,
          pkg.ownerEntityId,
        ],
      });
    }
  };

  const addItem = useMutation({
    mutationFn: (templateId: string) => collectionPackagesApi.addItem(id, templateId),
    onSuccess: () => {
      toast.success("Item added.");
      setTemplateToAdd("");
      invalidate();
    },
    onError: (e: Error) => toast.error(extractErrorMessage(e)),
  });

  // WS1 — per-item Archive / Unarchive / soft-Delete. The legacy Draft-only
  // hard-delete (removeItem) is replaced by soft-Delete in the OverflowMenu.
  const archiveItem = useMutation({
    mutationFn: (itemId: string) => collectionPackagesApi.archiveItem(id, itemId),
    onSuccess: () => {
      toast.success("Item archived.");
      invalidate();
    },
    onError: (e: Error) => toast.error(extractErrorMessage(e)),
  });

  const unarchiveItem = useMutation({
    mutationFn: (itemId: string) => collectionPackagesApi.unarchiveItem(id, itemId),
    onSuccess: () => {
      toast.success("Item restored.");
      invalidate();
    },
    onError: (e: Error) => toast.error(extractErrorMessage(e)),
  });

  const deleteItem = useMutation({
    mutationFn: (itemId: string) => collectionPackagesApi.deleteItem(id, itemId),
    onSuccess: () => {
      toast.success("Item deleted.");
      invalidate();
    },
    onError: (e: Error) => toast.error(extractErrorMessage(e)),
  });

  const invitation = usePortalInvitation({
    onSuccess: (result) =>
      toast.success(
        result.mode === "notified"
          ? "Account already active — sign-in notification sent."
          : "Set-password invitation sent.",
      ),
  });

  const sendMut = useMutation({
    mutationFn: async () => {
      setSendProblems([]);
      const sent = await collectionPackagesApi.send(id);
      // Tier 2 #9 — re-sends omit accountType; the server reads it from
      // the recipient's already-linked PortalUser. First-time issues
      // from the create-page path always supply it.
      await invitation.mutateAsync({ packageId: id });
      return sent;
    },
    onSuccess: () => {
      toast.success("Package sent — invitation emailed.");
      invalidate();
      invalidateApprovalChainReads(qc);
    },
    onError: (e: Error) => {
      const problems = sendBlockedProblems(e);
      if (problems) setSendProblems(problems);
      else toast.error(extractErrorMessage(e));
      invalidateApprovalChainReads(qc);
    },
  });

  const cancelMut = useMutation({
    mutationFn: () => collectionPackagesApi.cancel(id),
    onSuccess: () => {
      toast.success("Package cancelled.");
      invalidate();
    },
    onError: (e: Error) => toast.error(extractErrorMessage(e)),
  });

  // External Portal v2 PR 3 §10.5 / §10.3 — package-level archive +
  // on-demand reminder mirroring the staging-queue actions, so staff
  // working from the detail page have the same control set.
  const archiveMut = useMutation({
    mutationFn: () => collectionPackagesApi.archive(id),
    onSuccess: () => {
      toast.success("Package archived.");
      invalidate();
    },
    onError: (e: Error) => toast.error(extractErrorMessage(e)),
  });
  const remindMut = useMutation({
    mutationFn: () => collectionPackagesApi.remind(id),
    onSuccess: () => {
      toast.success("Reminder sent to recipient.");
      invalidate();
    },
    onError: (e: Error) => toast.error(extractErrorMessage(e)),
  });
  // WS1 — soft-Delete the whole package. Offered only when every (visible) item
  // is untouched; the server (SoftDeletePackageAsync) is authoritative and
  // 400s if any item — including a hidden archived one — has been acted on.
  const deletePackageMut = useMutation({
    mutationFn: () => collectionPackagesApi.deletePackage(id),
    onSuccess: () => {
      toast.success("Package deleted.");
      invalidate();
    },
    onError: (e: Error) => toast.error(extractErrorMessage(e)),
  });

  if (pkgQuery.isLoading) {
    return (
      <div className="py-space-6 text-center text-small text-gray-mid">Loading…</div>
    );
  }
  if (!pkg) {
    return (
      <div className="flex flex-col gap-space-3">
        <Link
          to="/admin/portal-accounts"
          className="inline-flex items-center gap-space-1 text-caption text-gold-shadow hover:text-gold"
        >
          <ArrowLeft size={12} aria-hidden /> Back
        </Link>
        <p className="text-small text-status-error">
          {pkgQuery.error instanceof Error
            ? pkgQuery.error.message
            : "Package not found."}
        </p>
      </div>
    );
  }

  const isDraft = pkg.status === "Draft";
  const recipient = recipientQuery.data;
  // WS1 — package soft-Delete is offered only when every (visible) item is
  // untouched. When "Show archived" is off, archived items aren't loaded, so
  // this is a best-effort FE gate; the server (SoftDeletePackageAsync) re-checks
  // every item — including hidden archived ones — and 400s otherwise.
  const packageDeletable =
    pkg.items.length > 0 && pkg.items.every((i) => i.canHardDelete);
  const anyArchivedItem = pkg.items.some((i) => i.isArchived);
  // Approval chains — an item routes through a chain when the server says so,
  // or (a draft) when its template carries one.
  const templateChain = new Map(templates.map((t) => [t.id, t.approvalChain ?? null] as const));
  const isChainItem = (item: CollectionPackageDto["items"][number]) =>
    item.approvalChainStatus != null || templateChain.get(item.templateId) != null;
  const draftChainItemIds = isDraft
    ? pkg.items.filter((i) => !i.isArchived && isChainItem(i)).map((i) => i.id)
    : [];
  const chainSend = chainReadiness.summarize(draftChainItemIds);
  const dealId = pkg.ownerEntityType === "Deal" ? pkg.ownerEntityId : null;

  return (
    <div className="flex flex-col gap-space-4">
      <Link
        to={fromAccountId ? `/admin/portal-accounts/${fromAccountId}` : "/admin/portal-accounts"}
        className="inline-flex items-center gap-space-1 text-caption text-gold-shadow hover:text-gold"
      >
        <ArrowLeft size={12} aria-hidden />{" "}
        {fromAccountId
          ? `Back to ${fromAccountQuery.data?.fullName ?? "account"}`
          : "Back to External Collection"}
      </Link>

      <PageHeader
        eyebrow={(() => {
          const ref = { type: pkg.ownerEntityType, id: pkg.ownerEntityId };
          const lbl = owners.lookup(ref);
          const pretty = pkg.ownerEntityType === "LegalEntity" ? "Entity" : pkg.ownerEntityType;
          return `${pretty} · ${lbl ? lbl.label : fallbackOwnerLabel(ref)}`;
        })()}
        title={`PACKAGE #${pkg.id.slice(0, 8).toUpperCase()}`}
        actions={
          <div className="flex items-center gap-space-2 flex-wrap">
            {isDraft && canSend && (
              <Button
                type="button"
                size="sm"
                onClick={() => sendMut.mutate()}
                disabled={sendMut.isPending || pkg.items.length === 0 || !chainSend.ready}
                loading={sendMut.isPending}
              >
                <Send size={14} aria-hidden /> Send
              </Button>
            )}
            {!isDraft && pkg.status !== "Cancelled" && canSend && (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => invitation.mutate({ packageId: id })}
                disabled={invitation.isPending}
                loading={invitation.isPending}
              >
                <Mail size={14} aria-hidden /> Resend invitation
              </Button>
            )}
            {!isDraft
              && pkg.status !== "Cancelled"
              && pkg.status !== "Completed"
              && pkg.status !== "Archived"
              && canSend && (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => remindMut.mutate()}
                disabled={remindMut.isPending}
                loading={remindMut.isPending}
                title="Send a reminder email to the recipient"
              >
                <Send size={14} aria-hidden /> Send reminder
              </Button>
            )}
            {pkg.status !== "Cancelled" && pkg.status !== "Completed" && canManage && (
              <Button
                type="button"
                size="sm"
                variant="destructive"
                onClick={() => cancelMut.mutate()}
                disabled={cancelMut.isPending}
                loading={cancelMut.isPending}
              >
                <XCircle size={14} aria-hidden /> Cancel
              </Button>
            )}
            {pkg.status !== "Archived" && canArchive && (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => {
                  if (confirm("Archive this package? It will be hidden from active queues.")) {
                    archiveMut.mutate();
                  }
                }}
                disabled={archiveMut.isPending}
                loading={archiveMut.isPending}
                title="Move to archive bucket"
              >
                Archive
              </Button>
            )}
            {/* WS1 — soft-Delete (recoverable). Offered only when every item is
                untouched; hidden otherwise (touched → Archive-only). */}
            {packageDeletable && canArchive && (
              <Button
                type="button"
                size="sm"
                variant="destructive"
                onClick={() => {
                  if (
                    confirm(
                      "Delete this package? It will be removed from all views but can be recovered by an admin.",
                    )
                  ) {
                    deletePackageMut.mutate();
                  }
                }}
                disabled={deletePackageMut.isPending}
                loading={deletePackageMut.isPending}
                title="Delete this package (all items untouched)"
              >
                <Trash2 size={14} aria-hidden /> Delete
              </Button>
            )}
          </div>
        }
      />

      {isDraft && canSend && (
        <SendBlockedList
          title="Send is held until the approval chain is ready:"
          problems={chainSend.reasons}
          testId="chain-send-held"
        />
      )}
      <SendBlockedList
        title="The package was not sent. Fix each problem, then send again:"
        problems={sendProblems}
        testId="send-blocked-errors"
      />

      {/* Status + created date on their own row beneath the header. */}
      <div className="flex items-center gap-space-3 flex-wrap">
        <PackageStatusBadge status={pkg.status} />
        <span className="text-caption text-gray-mid">
          Created {dateTime(pkg.createdAt)}
        </span>
      </div>

      <section className="rounded-md border border-border-light bg-white-pure p-space-4 grid grid-cols-2 md:grid-cols-4 gap-space-3 text-small">
        <Field label="Recipient">
          {recipient ? (
            <>
              {recipient.fullName}
              {pkg.recipientRoleLabel ? ` · ${pkg.recipientRoleLabel}` : ""}
              <span className="block text-caption text-gray-mid">{recipient.email}</span>
            </>
          ) : (
            "Loading recipient…"
          )}
        </Field>
        <Field label="Sent">
          {pkg.sentAt ? dateTime(pkg.sentAt) : "—"}
        </Field>
        <Field label="Due">
          {pkg.dueDate ? formatCalendarDate(pkg.dueDate) : "—"}
        </Field>
        <Field label="Items">{pkg.items.length}</Field>
      </section>

      <section className="rounded-md border border-border-light bg-white-pure p-space-4 flex flex-col gap-space-3">
        <header className="flex items-center justify-between">
          <h2 className="text-small font-semibold text-onyx m-0">Items</h2>
          {/* WS1 — URL-persisted "Show archived" toggle (parsed at render). */}
          {(canArchive || anyArchivedItem) && (
            <label className="inline-flex items-center gap-space-2 text-caption text-gray-mid">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
              />
              Show archived
            </label>
          )}
        </header>

        {pkg.items.length === 0 ? (
          <p className="text-caption text-gray-mid italic m-0">
            No items on this package yet.
          </p>
        ) : (
          <ul className="divide-y divide-border-light border border-border-light rounded-md m-0 p-0 list-none">
            {pkg.items.map((item) => {
              const submissions = submissionsByItem.get(item.id) ?? [];
              // WS1 — per-item action cluster. Archived rows offer Unarchive;
              // active rows offer Archive always + Delete only when untouched
              // (canHardDelete). Destructive Delete carries an inline confirm.
              const menuItems: OverflowMenuItem[] = item.isArchived
                ? [{ label: "Unarchive", onSelect: () => unarchiveItem.mutate(item.id) }]
                : [{ label: "Archive", onSelect: () => archiveItem.mutate(item.id) }];
              if (!item.isArchived && item.canHardDelete) {
                menuItems.push({
                  label: "Delete",
                  destructive: true,
                  confirm: "Delete this item? It can be recovered by an admin.",
                  onSelect: () => deleteItem.mutate(item.id),
                });
              }
              return (
                <li
                  key={item.id}
                  className={`flex flex-col${item.isArchived ? " opacity-60" : ""}`}
                >
                  <div className="flex items-center justify-between gap-space-3 p-space-3">
                    <div className="flex items-center gap-space-2 min-w-0">
                      <Badge status="info">{item.kind}</Badge>
                      <span className="text-small text-onyx truncate font-medium">
                        {item.templateName || `Item ${item.id.slice(0, 8)}`}
                      </span>
                      {item.isArchived && <Badge status="archived">Archived</Badge>}
                      {item.approvalChainStatusLabel && (
                        <Badge
                          status={
                            (item.approvalChainStatus &&
                              APPROVAL_CHAIN_STATUS_TONE[item.approvalChainStatus]) ||
                            "info"
                          }
                          data-testid={`item-chain-status-${item.id}`}
                        >
                          {item.approvalChainStatusLabel}
                        </Badge>
                      )}
                      {item.reviewerNote && (
                        <span className="text-caption text-gray-mid italic truncate">
                          “{item.reviewerNote}”
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-space-2">
                      <ItemStatusBadge status={item.status} />
                      {canArchive && (
                        <OverflowMenu items={menuItems} label="Item actions" />
                      )}
                    </div>
                  </div>
                  {isChainItem(item) && !item.isArchived && (
                    <div className="border-t border-border-light p-space-3">
                      {isDraft ? (
                        <ApprovalChainComposerPanel
                          packageId={pkg.id}
                          itemId={item.id}
                          itemName={item.templateName || "this item"}
                          dealId={dealId}
                          hasPermission={hasPermission}
                          onReadinessChange={chainReadiness.report}
                        />
                      ) : (
                        <ApprovalChainTimeline
                          itemId={item.id}
                          packageId={pkg.id}
                          hasPermission={hasPermission}
                          revise={{
                            templateId: item.templateId,
                            dealId,
                            recipientName: recipient?.fullName ?? null,
                          }}
                        />
                      )}
                    </div>
                  )}
                  {submissions.length > 0 && (
                    <ItemSubmissions
                      submissions={submissions}
                      onReview={(s) => setReviewingId(s.id)}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {isDraft && canManage && (
          <div className="flex gap-space-2">
            <Select
              value={templateToAdd}
              onChange={(e) => setTemplateToAdd(e.target.value)}
              disabled={templatesQuery.isLoading}
            >
              <option value="">
                {templatesQuery.isLoading ? "Loading templates…" : "Add a template…"}
              </option>
              {templates
                .filter((t) => t.active && !usedTemplateIds.has(t.id))
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} · {t.kind}
                  </option>
                ))}
            </Select>
            <Button
              type="button"
              variant="secondary"
              onClick={() => addItem.mutate(templateToAdd)}
              disabled={!templateToAdd || addItem.isPending}
              loading={addItem.isPending}
            >
              <Plus size={14} aria-hidden /> Add item
            </Button>
          </div>
        )}
      </section>

      {/* F7 — same review drawer as /collection/staging, opened in place. Its
          review mutations invalidate the ["collection-staging"] and
          ["collection-package"] prefixes, which cover this page's
          ["collection-staging","by-package",id] and ["collection-package",id,…]
          reads, so the submissions AND the item / package statuses refresh
          here after approve / file / return / reopen. */}
      <StagedSubmissionReviewDrawer
        submissionId={reviewingId}
        row={
          reviewingId
            ? (submissionsQuery.data?.items ?? []).find((s) => s.id === reviewingId) ?? null
            : null
        }
        onClose={() => setReviewingId(null)}
      />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col">
      <span className="text-caption uppercase tracking-wide text-gray-mid">{label}</span>
      <span className="text-small text-onyx mt-space-1">{children}</span>
    </div>
  );
}

// Review-surface consolidation §3 / F7, revised — per-item staged
// submissions. "Review" opens the SHARED review slide-over in place
// (page-level state), so staff keep the package context after
// approve / file / return. There is deliberately no second row-level link
// to the cross-account queue — that duplicate affordance was itself the
// stranding bug (clicking it navigated away and lost the package). The
// drawer's own header carries the one "Open in full queue" escape hatch.
// Sorted newest-first (the API returns them in that order).
function ItemSubmissions({
  submissions,
  onReview,
}: {
  submissions: StagedSubmissionListItem[];
  onReview: (s: StagedSubmissionListItem) => void;
}) {
  const { date } = useDateFormatter();
  return (
    <ul className="m-0 p-0 list-none border-t border-border-light bg-paper-warm/40">
      {submissions.map((s) => (
        <li key={s.id} className="border-b border-border-light last:border-b-0">
          <div className="flex items-center justify-between gap-space-3 py-space-2 pl-space-5 pr-space-3">
            <div className="flex items-center gap-space-2 min-w-0">
              <span className="text-caption text-gray-mid truncate">
                {s.originalFileName}
              </span>
              <span className="text-caption text-gray-mid">
                · {date(s.createdAt)}
              </span>
            </div>
            <div className="flex items-center gap-space-2">
              {(() => {
                const st = reviewRowStatus(s);
                return <Badge status={st.tone}>{st.label}</Badge>;
              })()}
              <button
                type="button"
                onClick={() => onReview(s)}
                className="inline-flex items-center text-caption font-medium text-onyx hover:text-gold-shadow"
              >
                Review
              </button>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}
